/**
 * StatusPage — `/status`: full collector-health diagnostics off `GET /api/status`
 * (the transparency surface behind the footer indicator). Shows the at-a-glance
 * level plus the raw signals: last success/attempt, data age, rows written,
 * source lag, consecutive failures, last failure reason.
 *
 * Same split as the other screens: a pure prop-driven `StatusView` (every state
 * testable) + a connected `StatusPage` that polls the endpoint.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useGetCollectorStatus } from "@/api/generated/status/status";
import type { CollectorStatusResponse } from "@/api/generated/model";
import { collectorSummary, formatCount, formatSeconds, type CollectorLevel } from "@/lib/status";
import { formatRomeDateTime } from "@/lib/time";
import { beatDurationStyle } from "@/lib/busyness";
import { HeartMark } from "@/components/HeartMark";

/**
 * The collector's health is the service's own pulse: a live collector beats a
 * calm, steady rhythm; a delayed one slows; an interrupted/unknown one goes
 * static (no false liveness). On-brand and meaningful — motion carries the data.
 */
const LEVEL_BEAT: Record<CollectorLevel, { dur: number; animate: boolean }> = {
  live: { dur: 1.7, animate: true },
  delayed: { dur: 2.6, animate: true },
  down: { dur: 1.7, animate: false },
  unknown: { dur: 1.7, animate: false },
};

const LEVEL_LEDE: Record<CollectorLevel, string> = {
  live: "Il raccoglitore sta salvando i dati in tempo reale.",
  delayed: "Gli aggiornamenti sono un po' in ritardo, ma il servizio è attivo.",
  down: "Gli aggiornamenti sono interrotti — i dati potrebbero non essere recenti.",
  unknown: "Stato del raccoglitore non ancora disponibile.",
};

/** How often to re-poll (the collector polls the feed every ~5 min). */
const REFETCH_INTERVAL_MS = 60_000;

interface QueryResult<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

interface StatusViewProps {
  status: QueryResult<CollectorStatusResponse>;
}

export function StatusView({ status }: StatusViewProps) {
  const { data, isPending, isError, refetch } = status;
  const summary = data ? collectorSummary(data) : undefined;
  const beat = LEVEL_BEAT[summary?.level ?? "unknown"];
  const lede = LEVEL_LEDE[summary?.level ?? "unknown"];

  return (
    <main className="detail">
      <nav className="detail__back" aria-label="Navigazione">
        <Link to="/" className="detail__back-link">
          ← Tutti i Pronto Soccorso
        </Link>
      </nav>

      <header className="detail-hero">
        <p className="hero__eyebrow">
          <span
            className={beat.animate ? "live-mark-wrap live-ring" : "live-mark-wrap"}
            style={beatDurationStyle(beat.dur)}
          >
            <HeartMark size={9} className={beat.animate ? "heart-mark" : ""} />
          </span>
          Trasparenza
        </p>

        <div className="detail-hero__headrow">
          <h1 className="detail__title">Stato del servizio</h1>
          {summary && (
            <span
              className={`status-badge collector-status--${summary.level}`}
              aria-label={`Stato: ${summary.word}`}
            >
              <span className="collector-status__dot" aria-hidden="true" />
              {summary.word}
            </span>
          )}
        </div>

        <p className="hero__lede">{lede}</p>
      </header>

      <section className="detail-panel" aria-labelledby="status-title" aria-busy={isPending}>
        <header className="detail-panel__head">
          <h2 id="status-title" className="detail-panel__title">
            Raccoglitore dati
          </h2>
        </header>

        <p className="estimate-disclaimer">
          Il raccoglitore interroga il feed CC0 ogni ~5 minuti e salva ogni rilevazione. Qui trovi
          quanto sono recenti i dati e se l'ultimo aggiornamento è andato a buon fine.
        </p>

        {isPending && <div className="heatmap__skeleton" aria-hidden="true" />}

        {isError && !isPending && (
          <div className="detail-panel__error" role="alert">
            <p>Impossibile caricare lo stato del servizio.</p>
            <button type="button" className="detail-panel__retry" onClick={refetch}>
              Riprova
            </button>
          </div>
        )}

        {data && !isError && !isPending && (
          <dl className="status-grid">
            <Row term="Ultimo aggiornamento riuscito">
              {formatRomeDateTime(data.lastSuccessAt)}
              {/* Keep the "· <age>" suffix as one non-breaking unit so the short
                  relative label never strands on its own line at narrow widths (#112). */}
              {summary?.ageLabel ? (
                <>
                  {" "}
                  <span className="status-grid__age">· {summary.ageLabel}</span>
                </>
              ) : null}
            </Row>
            <Row term="Ultimo tentativo">{formatRomeDateTime(data.lastAttemptAt)}</Row>
            <Row term="Età dei dati">{formatSeconds(data.dataAgeSeconds)}</Row>
            <Row term="Righe salvate (ultimo successo)">{formatCount(data.lastRowsWritten)}</Row>
            <Row term="Ritardo della sorgente">{formatSeconds(data.lastSourceLagSeconds)}</Row>
            <Row term="Errori consecutivi">{data.consecutiveFailures}</Row>
            <Row term="Ultimo errore">{data.lastFailureReason ?? "nessuno"}</Row>
            <Row term="Risposta generata">{formatRomeDateTime(data.generatedAt)}</Row>
          </dl>
        )}
      </section>
    </main>
  );
}

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="status-grid__row">
      <dt className="status-grid__term">{term}</dt>
      <dd className="status-grid__desc">{children}</dd>
    </div>
  );
}

export default function StatusPage() {
  const query = useGetCollectorStatus({ query: { refetchInterval: REFETCH_INTERVAL_MS } });

  return (
    <StatusView
      status={{
        data: query.data,
        isPending: query.isPending,
        isError: query.isError,
        refetch: () => void query.refetch(),
      }}
    />
  );
}
